import React from 'react'
import { forwardRef } from '@grupoboticario/flora-react-system'
import { FloraCheckboxProps } from './types'
import { useFloraCheckbox } from './useFloraCheckbox'
import {
  StyledCheckboxWrapper,
  StyledCheckboxInput,
  StyledCheckboxLabel,
  StyledCheckboxSpan,
} from './styles'

/**
 * @component
 * @name FloraCheckbox
 * @description Também conhecido como caixa de seleção, é empregado quando existe uma lista de opções e o usuário pode escolher várias alternativas, incluindo todas ou nenhuma. Cada FloraCheckbox é independente dos demais na lista, por isso selecionar uma caixa de seleção não afeta o estado dos outros itens.
 * @tutorial https://florabook.gb.tech/v2/index.html?path=/docs/core-components-floracheckbox
 */
export const FloraCheckbox = forwardRef<'input', FloraCheckboxProps>(
  (
    {
      id,
      textSize = 'medium',
      status = 'neutral',
      surfaceColor = 'light',
      indeterminate,
      label,
      disabled,
      hasIdentation = false,
      'autotagging-flora': autotaggingFlora = 'off',
      'data-autotagging-flora-section': dataAutotaggingFloraSection = '',
      'data-flora-text': dataFloraText,
      'aria-label': ariaLabel,
      'aria-disabled': ariaDisabled,
      ...props
    },
    forwardedRef,
  ) => {
    const { ref } = useFloraCheckbox({ forwardedRef, indeterminate })

    return (
      <StyledCheckboxWrapper>
        <StyledCheckboxInput
          {...props}
          id={id}
          ref={ref}
          as="input"
          data-flora="checkbox"
          autotagging-flora={autotaggingFlora}
          data-flora-text={dataFloraText || ariaLabel || label}
          data-autotagging-flora-section={dataAutotaggingFloraSection}
          type="checkbox"
          surfaceColor={surfaceColor}
          textSize={textSize}
          status={status}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-invalid={status === 'error'}
          aria-disabled={ariaDisabled}
          hasIdentation={hasIdentation}
        />
        <StyledCheckboxLabel htmlFor={id} disabled={Boolean(disabled || ariaDisabled)}>
          <StyledCheckboxSpan />
          {label}
        </StyledCheckboxLabel>
      </StyledCheckboxWrapper>
    )
  },
)
